import { formatISO } from 'date-fns';
import { type MonetaryValue } from 'ezmoney';

import { useInfiniteQuery } from 'src/core/api/hooks/useInfiniteQuery';
import { type InfiniteQueryState } from 'src/core/api/queryState';

import { type BreakdownPart } from '../../../../../models/breakdown';
import {
  type NetAmountStatus,
  type BreakdownSpending,
  type SpendingType,
} from '../../../../../models/breakdownSpending';
import { type ExpenseCategoryBreakdown } from '../../../../../models/expenseCategoryBreakdown';
import type { PeriodRangePayload } from '../../../../../models/periodRange';

const pageSize = 20; // hardcoded in the backend side

// Do not export the API type
type BreakdownSpendingApi = {
  id: string;
  date: string;
  description?: string;

  payer: {
    lastName: string;
    firstName: string;
    avatar?: string;
  };
  supplier?: string;

  netAmountStatus: NetAmountStatus;
  initialGlobalAmount?: MonetaryValue;
  globalAmount: MonetaryValue;
  amount: MonetaryValue;
  grossAmount: MonetaryValue;
  vatAmount?: MonetaryValue;

  document?: { filename: string; mediaType: string };

  isSplit: boolean;

  expenseCategoryId?: string;
  payableId?: string;
  entity?: {
    type: 'payable';
    id: string;
  };
};

export const useBudgetBreakdownSpendingsQuery = (parameters: {
  budgetId: string;
  breakdownPart: BreakdownPart;
  spendingType: SpendingType;
  breakdownComputedAt: Date;
  periodRange: PeriodRangePayload;
  expenseCategory?: ExpenseCategoryBreakdown['expenseCategory'];
}): InfiniteQueryState<BreakdownSpending[]> => {
  const params = transformToQueryParameters(parameters);

  return useInfiniteQuery<BreakdownSpending, BreakdownSpendingApi[]>({
    key: ['budgets', parameters.budgetId, 'breakdown', 'spendings', params],
    getRequest(page = '1') {
      return {
        type: 'rest',
        target: 'companyAPI',
        endpoint: `/budgets/budgets/${parameters.budgetId}/breakdown/spendings`,
        params: { ...params, page },
      };
    },
    getNextPageParam(lastPage, allPages) {
      // FIXME: this currently does not work due to the aggregation
      //        done in the controller side, this is not guaranteed that
      //        the number of items per page is respected.
      if (lastPage.length < pageSize) {
        return undefined;
      }
      const nextPage = allPages.length + 1;
      return nextPage.toString();
    },
    reshapeData: (spendings) =>
      spendings.map<BreakdownSpending>((spending) => ({
        ...spending,
        date: new Date(spending.date),
        document: spending.document
          ? {
              attachedAt: '', // Not provided by the API
              filename: spending.document.filename,
              mediaType: spending.document.mediaType,
            }
          : undefined,
      })),
  });
};

/**
 * Transform query arguments into query parameters that will be appended to rest API URL.
 * @param arguments Query arguments.
 * @returns the query parameters of the useBudgetBreakdownSpendings query as an Object.
 * @info useQuery ignores query parameters with `undefined` value.
 */
const transformToQueryParameters = ({
  periodRange,
  expenseCategory,
  ...queryArguments
}: Parameters<typeof useBudgetBreakdownSpendingsQuery>['0']) => {
  // If the expense category is defined (`isOther` is falsy), we don't want `unassignedOnly` query parameter
  const unassignedOnly = expenseCategory?.isOther || undefined;

  const periodParams = periodRange && {
    startDate:
      periodRange.from &&
      formatISO(periodRange.from, { representation: 'date' }),
    endDate:
      periodRange.to && formatISO(periodRange.to, { representation: 'date' }),
  };

  return {
    ...queryArguments,
    ...periodParams,
    expenseCategoryId: expenseCategory?.id,
    unassignedOnly,
  };
};
